import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from '../../../components/avatar';
import { ReactComponent as ChevronRightIcon } from '../../../icons/svg/arrow/arrow-chevron-right.svg';
import { Badge } from '../../../components/badge';
import type { HeaderNavLinks, HeaderUserData } from './Header';
import { RoccoLink } from '../../../components/link/RoccoLink';
import { Accordion, AccordionContent } from '../../../components/accordion';
import { AccordionItem, AccordionTrigger } from '../../../components/accordion';

interface MobileProfileMenuProps {
    userData: HeaderUserData;
    profileLinks: HeaderNavLinks;
    showProfileMenu: boolean;
    setShowProfileMenu: (val: boolean) => void;
    onLogout: () => void;
    currentCountryCode?: string;
}

/**
 * Custom hook to manage link loading states
 */
const useLinkLoadingState = () => {
    // tracking the clicked link pathname for compare with current url
    const [linkClicked, setLinkClicked] = useState<string | null>(null);
    // tracking the loading state
    const [isLoading, setIsLoading] = useState(false);
    // tracking the clicked link item title for applying loading effect to the clicked item only.
    const [isClicked, setIsClicked] = useState<string | null>(null);

    useEffect(() => {
        if (linkClicked === null) {
            return;
        } else {
            const currentURL = window.location.pathname;
            // if the current url is not the same as the clicked link, set the loading state to true
            const isloading = currentURL !== linkClicked;
            setIsLoading(isloading);

            // if the current url is the same as the clicked link, reset the loading state
            if (currentURL === linkClicked) {
                setLinkClicked(null);
                setIsClicked(null);
                setIsLoading(false);
            }
        }
    }, [linkClicked]);

    // handle the link click event
    const handleLinkClick = (title: string) => {
        setLinkClicked(title);
    };

    return {
        isLoading,
        isClicked,
        handleLinkClick,
        setIsClicked,
    };
};

/**
 * Renders a menu that can be open with a sliding motion.
 * Clicking outside the menu will automatically close it.
 */
const MobileProfileMenu = (props: MobileProfileMenuProps) => {
    const {
        userData,
        profileLinks,
        showProfileMenu,
        setShowProfileMenu,
        onLogout,
    } = props;

    const {
        firstName,
        lastName = '',
        avatarSrc,
        professionalSections,
        showCountryLabels,
    } = userData;

    // Determine what the heading should be
    const sectionHeading = useMemo(() => {
        const totalProfessionals = professionalSections.reduce(
            (count, section) => count + section.professionals.length,
            0,
        );
        if (totalProfessionals === 0) {
            return null;
        }
        if (totalProfessionals === 1) {
            return 'View business inbox';
        }
        return 'Your business inboxes';
    }, [professionalSections]);

    const [draggedStartY, setDraggedStartY] = useState(0);
    const [draggedDistance, setDraggedDistance] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [translateY, setTranslateY] = useState('100%');
    const menuRef = useRef<HTMLDivElement>(null);
    const transitionTimeout = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    // Captures the initial touch position
    const startDrag = (e: React.TouchEvent<HTMLElement>) => {
        const touch = e.touches[0];
        setDraggedStartY(touch?.clientY || 0);
    };

    // Handles the dragging movement and updates the menu's vertical position
    const dragging = (e: React.TouchEvent<HTMLElement>) => {
        const touch = e.touches[0];
        if (touch) {
            const distance = touch.clientY - draggedStartY;
            // Limit the upward movement to -80px and downward movement to positive values
            const clampedDistance = Math.max(
                -80,
                Math.min(window.innerHeight, distance),
            );
            setDraggedDistance(clampedDistance);
            setTranslateY(`${clampedDistance}px`);
        }
    };

    // Determines whether to close the menu based on drag distance
    const endDrag = () => {
        if (draggedDistance > 96) {
            setShowProfileMenu(false);
        } else if (draggedDistance > 0) {
            setTranslateY('0');
        }
        setDraggedDistance(0);
    };

    // Closes the menu with a slight delay for the exit animation
    const closeMenu = () => {
        setTranslateY('100%');
        transitionTimeout.current = setTimeout(() => {
            setShowProfileMenu(false);
            transitionTimeout.current = null;
        }, 200);
    };

    // Updates the menu's position when showProfileMenu changes
    useEffect(() => {
        if (showProfileMenu) {
            // First make the element visible
            setIsVisible(true);
            // Then trigger the animation in the next frame
            requestAnimationFrame(() => {
                setTranslateY('0');
            });
        } else {
            setTranslateY('100%');
            // Delay hiding the element until after the animation
            const timeout = setTimeout(() => {
                setIsVisible(false);
            }, 200); // Match your transition duration
            return () => clearTimeout(timeout);
        }
    }, [showProfileMenu]);

    return (
        <main
            className="scrollbar-hide fixed inset-x-0 top-0 z-100 h-screen w-screen overflow-scroll overscroll-y-none text-gray-900"
            style={{
                display: isVisible ? 'block' : 'none',
                touchAction: 'none', // Prevent default touch behaviors
            }}
        >
            {/* Transparent button to close the menu */}
            <button
                className="block h-20 w-full"
                onClick={closeMenu}
                aria-label="Close profile menu"
            ></button>

            {/* Menu container with slide-up animation */}
            <div
                className="bg-gray-100/85 h-screen w-screen overflow-hidden rounded-t-2xl backdrop-blur-lg transition-transform delay-0 duration-200 ease-in-out will-change-transform"
                style={{
                    transform: `translate3d(0, ${translateY}, 0)`,
                    WebkitTransform: `translate3d(0, ${translateY}, 0)`,
                    touchAction: 'none', // Prevent default touch behaviors
                }}
                ref={menuRef}
            >
                {/* Drag handle to allow closing the menu */}
                <button
                    className="flex w-full items-center justify-center pb-8 pt-3"
                    onTouchStart={startDrag}
                    onTouchMove={dragging}
                    onTouchEnd={endDrag}
                    aria-label="Drawer Handle"
                >
                    <span className="h-1 w-9 rounded bg-gray-500 opacity-30"></span>
                </button>

                {/* Container for the menu content, height is calculated as the full screen height minus the top gap (h-20) */}
                <div className="h-[calc(100dvh-80px)] overflow-y-auto">
                    {/* User Profile Information */}
                    <RoccoLink
                        to="/member/settings/edit-profile"
                        className="flex items-center gap-x-5 px-6"
                        isNonRemixRoute
                    >
                        <div className="text-gray-50">
                            <Avatar size="square64">
                                <AvatarImage src={avatarSrc} />
                                <AvatarFallback>
                                    {firstName[0]}
                                    {lastName[0]}
                                </AvatarFallback>
                            </Avatar>
                        </div>
                        <h2 className="text-xl font-medium">
                            {firstName} {lastName}
                        </h2>
                    </RoccoLink>

                    {/* Profile Navigation Links */}
                    <nav className="mt-8">
                        {profileLinks.links.map((item) => (
                            <ProfileMenuLink
                                title={item.title}
                                link={item.to}
                                key={item.title}
                                badgeCount={item.badgeCount}
                                isNonRemixRoute={item.isNonRemixRoute}
                            />
                        ))}

                        <ProfileMenuLink
                            title={'Log out'}
                            link={'#'}
                            onClick={(e) => {
                                e.preventDefault();
                                onLogout();
                            }}
                        />

                        {/* Professional Accounts Section */}
                        {professionalSections.length > 0 && (
                            <div className="mt-6">
                                {/* Always show heading when there are professionals */}
                                {sectionHeading && (
                                    <h3 className="px-6 pt-4 text-base font-medium">
                                        {sectionHeading}
                                    </h3>
                                )}

                                {/* Render professionals grouped by country */}
                                {professionalSections.map((section) => (
                                    <div key={section.country}>
                                        {/* Show country label only when multiple countries */}
                                        {showCountryLabels && (
                                            <h3 className="px-6 pt-8 text-base text-gray-500">
                                                {section.label}
                                            </h3>
                                        )}
                                        {section.professionals.map((item) => {
                                            const logoSrc =
                                                item.companyLogo96Resized ||
                                                item.companyLogo48Resized ||
                                                item.logo;
                                            return (
                                                <ProfileMenuLinkButton
                                                    key={item.id}
                                                    title={item.title}
                                                    link={`/business/${item.urlSegment}/inbox`}
                                                    isNonRemixRoute
                                                    logoSrc={logoSrc}
                                                    logoBackgroundColor={
                                                        item.logoBackgroundColor
                                                    }
                                                    badgeCount={item.unreads}
                                                />
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        )}
                    </nav>

                    {/* Placeholder for bottom browser navigation */}
                    <div className="h-44"></div>
                </div>
            </div>
        </main>
    );
};

interface ProfileMenuLinkProps {
    title: string;
    link: string;
    isNonRemixRoute?: boolean;
    badgeCount?: number;
    logoSrc?: string;
    logoBackgroundColor?: string;
    onClick?: (e: React.MouseEvent) => void;
}

/**
 * Renders a navigation link or button inside the profile menu.
 * It can display a badge for notifications.
 *
 * @note: If the link has an onClick prop, it will prevent the default action of following the link.
 */
const ProfileMenuLink = ({
    title,
    link,
    isNonRemixRoute = false,
    badgeCount,
    onClick,
}: ProfileMenuLinkProps) => {
    const { isLoading, isClicked, handleLinkClick, setIsClicked } =
        useLinkLoadingState();

    return (
        <RoccoLink
            className={`flex items-center justify-between border-b border-b-black/10 px-6 py-4 text-gray-900 hover:text-gray-900 ${isClicked === title && isLoading ? 'bg-white opacity-70' : ''}`}
            to={link}
            prefetch="viewport"
            isNonRemixRoute={isNonRemixRoute}
            role={onClick ? 'button' : 'link'}
            onClick={(e) => {
                handleLinkClick(link);
                setIsClicked(title);
                if (onClick) {
                    e.preventDefault();
                    onClick(e);
                }
            }}
        >
            <span className="text-base">{title}</span>
            <span className="flex items-center">
                {!!badgeCount && (
                    <Badge
                        size="centeredCompact"
                        text={badgeCount > 9 ? 'small' : 'default'}
                    >
                        {badgeCount > 9 ? '9+' : badgeCount}
                    </Badge>
                )}
                <ChevronRightIcon className="ml-2 size-3" />
            </span>
        </RoccoLink>
    );
};

const ProfileMenuLinkButton = ({
    title,
    link,
    isNonRemixRoute = false,
    badgeCount,
    logoSrc,
    logoBackgroundColor,
    onClick,
}: ProfileMenuLinkProps) => {
    const { isLoading, isClicked, handleLinkClick, setIsClicked } =
        useLinkLoadingState();

    return (
        <Accordion type="single" collapsible>
            <AccordionItem value={title} variant="lightBorder">
                <div
                    className="px-6"
                    onClick={onClick}
                    aria-label={`Dropdown for ${title}`}
                    role="button"
                >
                    <AccordionTrigger startDirection="right">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-x-5">
                                <Avatar
                                    size="square48"
                                    className="shrink-0 p-1"
                                    style={{
                                        backgroundColor:
                                            logoBackgroundColor ??
                                            (logoSrc ? 'white' : '#C7C7C7'),
                                    }}
                                >
                                    <AvatarImage src={logoSrc} fit="contain" />
                                    <AvatarFallback className="bg-transparent" />
                                </Avatar>
                                <div className="wrap-break-word mr-4 text-left text-base">
                                    {title}
                                </div>
                            </div>
                        </div>
                    </AccordionTrigger>
                </div>

                <AccordionContent
                    className={`${isClicked === title && isLoading ? 'bg-white opacity-70' : ''}`}
                >
                    <RoccoLink
                        className="flex items-center justify-between border-t border-t-black/10 px-6 pt-4 text-gray-900 hover:text-gray-900"
                        to={link}
                        isNonRemixRoute={isNonRemixRoute}
                        onClick={() => {
                            handleLinkClick(link);
                            setIsClicked(title);
                        }}
                    >
                        <span>Company Inbox</span>
                        {!!badgeCount && (
                            <Badge size="centeredCompact" text="default">
                                {badgeCount > 9 ? '9+' : badgeCount}
                            </Badge>
                        )}
                    </RoccoLink>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
};

export default MobileProfileMenu;
